import type { ReactNode } from 'react';
import { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { useAuth } from '@/contexts/auth';
import { useSaveThemePreference } from '@/domain/users';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';

import { printInLightTheme } from './printTheme';
import {
    isTheme,
    readEligible,
    readStoredTheme,
    resolveTheme,
    systemPrefersDark,
    writeEligible,
    writeStoredTheme,
} from './themeStorage';
import { showTheme } from './themeTransition';
import type { Theme, ThemeActions, ThemeState } from './types';

export const ThemeStateContext = createContext<ThemeState | null>(null);
export const ThemeActionsContext = createContext<ThemeActions | null>(null);

function usePrefersDark(): boolean {
    const [prefersDark, setPrefersDark] = useState(systemPrefersDark);
    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return;
        const query = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);
    return prefersDark;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const flag = usePostHogFeatureFlag('dark-mode');
    const { user } = useAuth();
    const { save: saveThemePreference, isSaving } = useSaveThemePreference();
    const prefersDark = usePrefersDark();
    const [storedTheme, setStoredTheme] = useState<Theme | null>(readStoredTheme);

    // Until PostHog answers, trust the eligibility cached on the last visit so the theme the
    // pre-paint script applied stays put instead of flashing to light and back.
    const eligible = flag.isLoading ? readEligible() : flag.enabled;

    // An admin impersonating a customer keeps their own theme and never writes to the
    // customer's profile.
    const isImpersonating = !!user?.impersonatorUser;
    const rawProfileTheme = user?.profile?.theme;
    const profileTheme = !isImpersonating && isTheme(rawProfileTheme) ? rawProfileTheme : null;

    useEffect(() => {
        if (!flag.isLoading) writeEligible(flag.enabled);
    }, [flag.enabled, flag.isLoading]);

    // The profile is the source of truth across devices; mirror it so the next page load
    // paints the right theme before /me arrives. Not while a save is pending, when the
    // cached profile is older than the choice being saved.
    useEffect(() => {
        if (!isSaving && profileTheme && profileTheme !== readStoredTheme()) {
            writeStoredTheme(profileTheme);
            setStoredTheme(profileTheme);
        }
    }, [profileTheme, isSaving]);

    const theme = resolveTheme({ eligible, profileTheme, storedTheme, prefersDark, isSaving });

    useLayoutEffect(() => {
        showTheme(theme);
    }, [theme]);

    useEffect(() => printInLightTheme(theme), [theme]);

    const setTheme = useCallback(
        (next: Theme) => {
            if (!eligible || next === theme) return;
            const previous = storedTheme;
            writeStoredTheme(next);
            setStoredTheme(next);
            if (user && !isImpersonating) {
                saveThemePreference(next, {
                    onError: () => {
                        writeStoredTheme(previous);
                        setStoredTheme(previous);
                    },
                });
            }
        },
        [eligible, theme, storedTheme, user, isImpersonating, saveThemePreference]
    );

    const state = useMemo<ThemeState>(() => ({ theme, canToggle: eligible }), [theme, eligible]);
    const actions = useMemo<ThemeActions>(
        () => ({
            setTheme,
            toggleTheme: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
        }),
        [setTheme, theme]
    );

    return (
        <ThemeStateContext value={state}>
            <ThemeActionsContext value={actions}>{children}</ThemeActionsContext>
        </ThemeStateContext>
    );
}
